import React from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'

import RightArrowIcon from '@common/assets/svg/RightArrowIcon'
import WalletFilledIcon from '@common/assets/svg/WalletFilledIcon'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import spacings from '@common/styles/spacings'
import { BORDER_RADIUS_PRIMARY } from '@common/styles/utils/common'
import flexbox from '@common/styles/utils/flexbox'
import { FEATURES } from '@web/constants/features'
import { AnimatedPressable, useCustomHover } from '@web/hooks/useHover'

export type AddAccountType = 'basic' | 'smart'

const OptionRow = ({
  title,
  description,
  badge,
  onPress,
  testID
}: {
  title: string
  description: string
  badge: React.ReactNode
  onPress: () => void
  testID?: string
}) => {
  const { theme } = useTheme()
  const [bindAnim, animStyle] = useCustomHover({
    property: 'backgroundColor',
    values: {
      from: theme.secondaryBackground as string,
      to: theme.tertiaryBackground as string
    }
  })

  return (
    <AnimatedPressable
      onPress={onPress}
      testID={testID}
      style={[
        flexbox.directionRow,
        flexbox.alignCenter,
        spacings.ph,
        spacings.pv,
        {
          borderRadius: BORDER_RADIUS_PRIMARY,
          gap: 16
        },
        animStyle
      ]}
      {...bindAnim}
    >
      {badge}
      <View style={[flexbox.flex1, { gap: 4 }]}>
        <Text fontSize={16} weight="semiBold">
          {title}
        </Text>
        <Text fontSize={14} appearance="secondaryText">
          {description}
        </Text>
      </View>
      <RightArrowIcon />
    </AnimatedPressable>
  )
}

const IconBadge = ({
  backgroundColor,
  children
}: {
  backgroundColor: string
  children: React.ReactNode
}) => (
  <View
    style={[
      flexbox.center,
      {
        width: 36,
        height: 36,
        borderRadius: 12,
        backgroundColor
      }
    ]}
  >
    {children}
  </View>
)

const AddAccountTypeOptions = ({ onSelect }: { onSelect: (type: AddAccountType) => void }) => {
  const { t } = useTranslation()
  const { theme } = useTheme()

  return (
    <View style={{ gap: 12 }}>
      {FEATURES.smartAccountEnabled && (
        <OptionRow
          testID="add-smart-account-option"
          title={t('Smart Account')}
          description={t('Add the next Smart Account derived from this recovery phrase')}
          badge={
            <IconBadge backgroundColor={theme.successDecorative as string}>
              <Text fontSize={12} weight="semiBold" color={theme.primaryBackground}>
                SA
              </Text>
            </IconBadge>
          }
          onPress={() => onSelect('smart')}
        />
      )}
      <OptionRow
        testID="add-basic-account-option"
        title={t('Standard account')}
        description={t('Add the next standard account derived from this recovery phrase')}
        badge={
          <IconBadge backgroundColor={theme.primary as string}>
            <WalletFilledIcon width={18} height={19} color={theme.primaryBackground} />
          </IconBadge>
        }
        onPress={() => onSelect('basic')}
      />
    </View>
  )
}

export default React.memo(AddAccountTypeOptions)
